import { uniq } from 'lodash-es'
import { makeAutoObservable } from 'mobx'

import type CheckableMap from '@/models/CheckableMap'

import type { _MakeProblem } from './MakeProblem'

export class MakeProblemColl {
  constructor(private problemMap: CheckableMap<_MakeProblem['id'], _MakeProblem>) {
    makeAutoObservable(this)
  }

  get isEmpty() {
    return this.problemMap.toArr.length === 0
  }

  get toArr() {
    return [...this.problemMap.values()]
  }

  get entries() {
    return this.problemMap.entries()
  }

  get originalProblemList() {
    return this.toArr
      .map((problem, index) => ({
        id: problem.problemSource?.id,
        problemImageUrl: problem.problemSource?.problemImageUrl,
        relationProblemIndex: index,
        conceptName: problem.conceptName,
      }))
      .filter((v) => !!v.id)
      .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
  }

  get problemIdList() {
    return uniq(this.toArr.map(({ id }) => id))
  }

  get conceptIdList() {
    return uniq(this.toArr.map(({ conceptId }) => conceptId))
  }

  get curriculumIdList() {
    return uniq(this.toArr.map(({ curriculumId }) => curriculumId))
  }

  get baseWorkbookProblemIdList(): number[] {
    return uniq(
      this.toArr
        .map(({ baseWorkbookProblemId }) => baseWorkbookProblemId)
        .filter((id) => id !== null),
    ) as number[]
  }

  get isFullyChecked() {
    return this.problemMap.isFullyChecked
  }

  summary() {
    return this.toArr.map(({ id, type }) => ({
      id,
      type,
    }))
  }

  values() {
    return this.problemMap.values()
  }

  set(k, v) {
    return this.problemMap.set(k, v)
  }

  delete(id) {
    return this.problemMap.delete(id)
  }

  deletes(keys = [...this.problemMap.keys()]) {
    return this.problemMap.deletes(keys)
  }

  checkSize() {
    return this.problemMap.checkSize
  }

  toCheckedValues() {
    return this.problemMap.toCheckedValues
  }

  toCheckedSize() {
    return this.problemMap.toCheckedValues.length
  }

  get(id: _MakeProblem['id']) {
    return this.problemMap.get(id)
  }

  has(id: _MakeProblem['id']) {
    return this.problemMap.has(id)
  }

  toggleAllCheck() {
    return this.problemMap.toggleAllCheck()
  }

  clearAllCheck() {
    return this.problemMap.clearAllCheck()
  }

  setCheck(v: _MakeProblem['id']) {
    this.problemMap.setCheck(v)
  }

  isChecked(v: _MakeProblem['id']) {
    return this.problemMap.isChecked(v)
  }

  get isPartChecked() {
    return this.problemMap.isPartChecked
  }

  toggleCheck(v: _MakeProblem['id']) {
    this.problemMap.toggleCheck(v)
  }

  addProblem(problem: _MakeProblem) {
    this.problemMap.set(problem.id, problem)
  }

  adds(workbookColl: MakeProblemColl) {
    this.problemMap.adds(workbookColl.problemMap.entries())
  }

  updateProblem(id: _MakeProblem['id'], updateData: _MakeProblem) {
    const selected = this.problemMap?.get(id)
    if (selected) {
      selected.update(updateData)
    }
  }
}
